import { z } from 'zod';
import { ALLOWED_IMAGE_TYPES } from './constants';

// ── Primitive schemas ──────────────────────────────────────────────

/** Valid email format */
export const emailSchema = z.string().email('Invalid email format');

/** Password: minimum 8 characters */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters');

/**
 * URL slug: lowercase letters, numbers, and hyphens only.
 * Must start and end with an alphanumeric character.
 * No consecutive hyphens allowed.
 */
export const slugSchema = z
  .string()
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'Slug must contain only lowercase letters, numbers, and hyphens',
  );

/** Allowed image MIME types */
export const fileTypeSchema = z.enum(
  [...ALLOWED_IMAGE_TYPES] as [string, ...string[]],
  { message: 'Unsupported file type' },
);

/** Tax rate: number between 0 and 100 (inclusive) */
export const taxRateSchema = z
  .number()
  .min(0, 'Tax rate must be at least 0')
  .max(100, 'Tax rate must be at most 100');

// ── Composite form schemas ─────────────────────────────────────────

/** Login schema stores translation keys so visible errors follow language changes. */
export const createLoginSchema = () =>
  z.object({
    email: z
      .string()
      .trim()
      .min(1, 'auth.emailRequired')
      .email('auth.emailInvalid'),
    password: z.string().min(1, 'auth.passwordRequired'),
  });

/** Registration schema stores translation keys for reactive localization. */
export const createRegisterSchema = () =>
  z
    .object({
      email: z
        .string()
        .trim()
        .min(1, 'auth.emailRequired')
        .email('auth.emailInvalid'),
      password: z.string().min(8, 'auth.passwordMinLength'),
      confirmPassword: z.string().min(1, 'auth.confirmPasswordRequired'),
      nickname: z.string().trim().min(1, 'auth.nicknameRequired'),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: 'auth.passwordMismatch',
      path: ['confirmPassword'],
    });

/** Login form */
export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

/** Registration form */
export const registerSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    nickname: z.string().min(1, 'Nickname is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/** Password reset request form */
export const passwordResetSchema = z.object({
  email: emailSchema,
});

/** New password form (reset confirmation) */
export const newPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// ── Type exports ───────────────────────────────────────────────────

export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;
export type NewPasswordFormData = z.infer<typeof newPasswordSchema>;
